import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { Router } from '@angular/router';
import {
  ApiNewDokumentPowiazanyDalDto,
  AttachmentsComponent,
  DocSignService,
  FormAlertService,
  MapSettingsService,
  MapStateService,
  PortalId,
  PzService,
} from '@gk/gk-modules';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { forkJoin, switchMap, takeWhile, tap } from 'rxjs';
import { ProxyDetailsComponent } from '../../../components/proxy-details/proxy-details.component';
import { MainRoutes } from '../../../guards/guard.models';
import { NewRequestHelperService } from '../../../services/new-request-helper/new-request-helper.service';
import { BsMessageType } from '../../../services/request-workspace-state/request-workspace-state.model';
import { BaseNewRequestComponent } from '../../../shared/base-new-request/base-new-request.component';
import { InvestorDetailsComponent } from '../../../shared/investor-details/investor-details.component';
import { RequestReplyFormComponent } from '../../../shared/request-reply-form/request-reply-form.component';
import { DocsTableEditableComponent } from '../../designer-portal/new-designer-request/docs-table-editable/docs-table-editable.component';
import { WENewRequestControlName } from './services/new-request-form/new-request-form.model';
import { WENewRequestFormService } from './services/new-request-form/new-request-form.service';
import { WENewRequest } from './services/new-request/new-request.model';

@Component({
  selector: 'app-water-equipment-request',
  templateUrl: './water-equipment-request.component.html',
  standalone: false,
})
export class WaterEquipmentRequestComponent
  extends BaseNewRequestComponent
  implements OnInit, OnDestroy
{
  @ViewChild(InvestorDetailsComponent)
  investorDetailsComponent: InvestorDetailsComponent;
  @ViewChild(RequestReplyFormComponent)
  requestReplyFormComponent: RequestReplyFormComponent;
  @ViewChild(ProxyDetailsComponent)
  proxyDetailsComponent: ProxyDetailsComponent;
  @ViewChild(DocsTableEditableComponent)
  docsTableEditableComponent: DocsTableEditableComponent;
  @ViewChild(AttachmentsComponent) attachmentsComponent: AttachmentsComponent;
  override controlName = WENewRequestControlName;
  formValue: WENewRequest;
  formNotValidTranslation: string;
  override portalId =
    PortalId.EnvironmentalProtectionPortalWaterEquipmentRequest;

  constructor(
    public weNewRequestFormService: WENewRequestFormService,
    private toastr: ToastrService,
    public override translateService: TranslateService,
    public override docSignService: DocSignService,
    public override newRequestHelperService: NewRequestHelperService,
    public override router: Router,
    public override formAlertService: FormAlertService,
    public override pzService: PzService,
    public override mapSettingsService: MapSettingsService,
    public override mapStateService: MapStateService,
  ) {
    super(
      pzService,
      newRequestHelperService,
      docSignService,
      router,
      translateService,
      mapSettingsService,
      mapStateService,
      formAlertService,
    );
  }

  override ngOnInit(): void {
    this.createForm();
    super.subscribeToDocSignTranslations();
    this.subscribeToFormNotValidTranslation();
    this.fetchWrongFileText();
  }

  createForm(): void {
    this.formGroup = this.weNewRequestFormService.getFormGroup();
  }

  subscribeToFormNotValidTranslation(): void {
    this.translateService
      .get(
        'ENVIRONMENTAL_PROTECTION_PORTAL.WATER_EQUIPMENT.VALIDATION.FORM_NOT_VALID_ERROR',
      )
      .pipe(takeWhile(() => this.isAlive))
      .subscribe(
        (formNotValidTranslation) =>
          (this.formNotValidTranslation = formNotValidTranslation),
      );
  }

  async handleSubmit(): Promise<void> {
    this.submitted = true;
    if (!this.isRequestValid()) {
      this.toastr.error(this.formNotValidTranslation);
      return;
    }
    const apiFiles = await Promise.all(
      this.attachmentsComponent.getConvertedFiles(),
    ).catch(() => new Error());
    if (
      (Array.isArray(apiFiles) &&
        apiFiles.some((file) => file instanceof Error)) ||
      apiFiles instanceof Error
    ) {
      this.toastr.error(this.wrongFilesErrorText);

      return;
    }

    this.sendRequest(apiFiles as ApiNewDokumentPowiazanyDalDto[]);
  }

  private sendRequest(apiDocuments: ApiNewDokumentPowiazanyDalDto[]): void {
    forkJoin([
      this.investorDetailsComponent.askForPostOfficeWhenPostalCodeIsNotFromDictionary(),
      this.requestReplyFormComponent.askForPostOfficeWhenPostalCodeIsNotFromDictionary(),
      this.proxyDetailsComponent.askForPostOfficeWhenPostalCodeIsNotFromDictionary(),
    ])
      .pipe(
        tap(() => {
          this.docSignPending = true;
          this.setDocSignMsg(BsMessageType.Info, 'SENDING_REQUEST');
        }),
        switchMap(() =>
          this.docSignService.addToSign(
            WENewRequest.fromAppToApi(this.getFormValue(), apiDocuments),
            '/api/interesant/wniosek/nadanieNrSprzetuPlywajacego/addToSign',
          ),
        ),
        takeWhile(() => this.isAlive),
      )
      .subscribe({
        next: (addedDocToSignResponse) => {
          this.handleSendAndValidateSuccess(addedDocToSignResponse);
        },
        error: () => this.handleSendAndValidateFailure(),
      });
  }

  override handleDocSignSuccess(): void {
    this.docSignUrl = '';
    this.router.navigateByUrl(
      `/${MainRoutes.EnvironmentalProtectionPortalWaterEquipmentRequest}/requests-list`,
    );
  }

  isRequestValid(): boolean {
    return (
      this.formGroup.valid && this.attachmentsComponent.areDocumentsValid()
    );
  }

  getFormValue(): WENewRequest {
    return this.formGroup.getRawValue();
  }

  getBirthDateFormControl(): UntypedFormControl {
    return this.formGroup.get(
      WENewRequestControlName.BirthDate,
    ) as UntypedFormControl;
  }

  getBirthPlaceFormControl(): UntypedFormControl {
    return this.formGroup.get(
      WENewRequestControlName.BirthPlace,
    ) as UntypedFormControl;
  }

  getWatercraftTypeFormControl(): UntypedFormControl {
    return this.formGroup.get(
      WENewRequestControlName.WatercraftType,
    ) as UntypedFormControl;
  }

  getTechnicalConditionStatementCheckboxFormControl(): UntypedFormControl {
    return this.formGroup.get(
      WENewRequestControlName.TechnicalConditionStatementCheckbox,
    ) as UntypedFormControl;
  }

  getCommitmentStatementCheckboxFormControl(): UntypedFormControl {
    return this.formGroup.get(
      WENewRequestControlName.CommitmentStatementCheckbox,
    ) as UntypedFormControl;
  }

  getWatercraftTotalLengthFormControl(): UntypedFormControl {
    return this.formGroup.get(
      WENewRequestControlName.TotalLength,
    ) as UntypedFormControl;
  }

  getWatercraftSideHeightFormControl(): UntypedFormControl {
    return this.formGroup.get(
      WENewRequestControlName.SideHeight,
    ) as UntypedFormControl;
  }

  getWatercraftLargestWidthFormControl(): UntypedFormControl {
    return this.formGroup.get(
      WENewRequestControlName.SideHeight,
    ) as UntypedFormControl;
  }

  getWatercraftSeatsNumberFormControl(): UntypedFormControl {
    return this.formGroup.get(
      WENewRequestControlName.SideHeight,
    ) as UntypedFormControl;
  }
  getWatercraftConstructionMaterialTypeFormControl(): UntypedFormControl {
    return this.formGroup.get(
      WENewRequestControlName.ConstructionMaterialType,
    ) as UntypedFormControl;
  }

  getWatercraftStagingPointFormControl(): UntypedFormControl {
    return this.formGroup.get(
      WENewRequestControlName.WatercraftType,
    ) as UntypedFormControl;
  }

  getWatercraftDriveTypeFormControl(): UntypedFormControl {
    return this.formGroup.get(
      WENewRequestControlName.DriveType,
    ) as UntypedFormControl;
  }

  checkInvestorDetailsFormGroupAndBirthPlaceDateFormControlsValidAndFormValidated(): boolean {
    return (
      (!this.getInvestorDetailsFormGroup().valid && this.submitted) ||
      (!this.getBirthPlaceFormControl().valid && this.submitted) ||
      (!this.getBirthDateFormControl().valid && this.submitted)
    );
  }

  checkWatercraftDimensionsFormControlsValidAndFormValidated(): boolean {
    return (
      (!this.getWatercraftTotalLengthFormControl().valid && this.submitted) ||
      (!this.getWatercraftSideHeightFormControl().valid && this.submitted) ||
      (!this.getWatercraftLargestWidthFormControl().valid && this.submitted) ||
      (!this.getWatercraftSeatsNumberFormControl().valid && this.submitted) ||
      (!this.getWatercraftConstructionMaterialTypeFormControl().valid &&
        this.submitted)
    );
  }

  checkWatercraftDataFormControlsValidAndFormValidated(): boolean {
    return (
      (!this.getWatercraftTypeFormControl().valid && this.submitted) ||
      (!this.getWatercraftStagingPointFormControl().valid && this.submitted) ||
      (!this.getWatercraftDriveTypeFormControl().valid && this.submitted)
    );
  }

  checkTechnicalConditionStatementCheckboxFormControlValidAndFormValidated(): boolean {
    return (
      !this.getTechnicalConditionStatementCheckboxFormControl().valid &&
      this.submitted
    );
  }

  checkCommitmentStatementCheckboxFormControlValidAndFormValidated(): boolean {
    return (
      !this.getCommitmentStatementCheckboxFormControl().valid && this.submitted
    );
  }

  ngOnDestroy(): void {
    this.isAlive = false;
  }
}
